import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  Combobox,
  FilterField,
  LicensePlate,
  Pagination,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from "@rumbledirect/ui-v2";
import {
  getGetAdminVehicleHandoversQueryKey,
  useGetAdminVehicleHandovers,
} from "@/lib/api/generated/vehicle-handover/vehicle-handover";
import { getAffiliateId } from "@/auth/authStore";
import { EMPTY_VALUE, formatDateOrEmpty } from "@rumbledirect/ui-v2/format";
import type {
  VehicleHandover,
  VehicleHandoverDestination,
  VehicleHandoverType,
} from "@/vehicleHandovers/vehicleHandover";
import {
  DEFAULT_HANDOVER_STATUS_FILTER,
  handoverWorkLabels,
  handoverDestinationLabel,
  handoverDestinationOptions,
  handoverReasonLabel,
  handoverStatusFilterOptions,
  handoverStatusFilterQuery,
  handoverStatusLabel,
  handoverStatusTone,
  type VehicleHandoverStatusFilter,
} from "@/vehicleHandovers/vehicleHandoverLabels";
import { VehicleHandoverFollowUpOverdueBadge } from "@/vehicleHandovers/VehicleHandoverFollowUpOverdueBadge";
import {
  booleanFilterOptions,
  handoverTypeLabel,
  handoverTypeOptions,
  parseBooleanFilter,
  parseVehicleHandoversSearch,
  type VehicleHandoversSearch,
} from "@/vehicleHandovers/vehicleHandoversSearch";

/** Matches `paginationItemsPerPage` on the backend resource. */
const ITEMS_PER_PAGE = 20;

/** Plate, vehicle, type, employee, reason, destination, planned, status, open. */
const COLUMN_COUNT = 9;

// One skeleton per row of a full page, so the table keeps its height when the
// real rows arrive instead of jumping.
const SKELETON_ROW_KEYS = Array.from(
  { length: ITEMS_PER_PAGE },
  (_, index) => `vehicle-handover-skeleton-${index}`,
);

export const Route = createFileRoute("/_authenticated/vehicle-handovers")({
  // The filter state lives in the URL so a view can be shared or bookmarked.
  // A value the API does not know is normalised away to `undefined` rather than
  // omitted: the router merges what this returns over the raw search params, so
  // only an explicit `undefined` actually clears one.
  validateSearch: parseVehicleHandoversSearch,
  component: VehicleHandoversPage,
});

function VehicleHandoversPage(): ReactNode {
  const {
    page,
    status,
    destination,
    type,
    overdue,
    followUpPending,
    followUpOverdue,
    swap,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  const params = {
    page,
    ...handoverStatusFilterQuery(status),
    ...(destination ? { destination } : {}),
    type,
    overdue,
    followUpPending,
    followUpOverdue,
    swap,
  };
  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminVehicleHandovers(params, {
      query: {
        queryKey: [
          ...getGetAdminVehicleHandoversQueryKey(params),
          getAffiliateId(),
        ],
        placeholderData: keepPreviousData,
      },
    });

  const totalItems = data?.totalItems ?? 0;

  // Changing a filter resets to the first page: page 4 of the old result set
  // rarely exists in the new one, and landing on an empty page reads as "no
  // results" rather than "wrong page".
  const setFilter = (patch: Partial<VehicleHandoversSearch>): void => {
    void navigate({
      search: (prev) => ({ ...prev, ...patch, page: 1 }),
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Innames</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Voertuigen die worden ingenomen, van aanmelding tot afronding.
        </p>
      </div>

      <fieldset className="flex flex-wrap items-end gap-3 rounded-2xl border border-border/70 bg-surface p-4 shadow-xs">
        <legend className="sr-only">Filter innames</legend>

        <FilterField label="Status">
          <Combobox
            aria-label="Status"
            value={status}
            onValueChange={(value) =>
              // Clearing the filter is deselecting "Actueel" or "Alle" as much
              // as deselecting a status, so it lands back on the work list
              // rather than on an empty control.
              setFilter({
                status:
                  (value as VehicleHandoverStatusFilter | undefined) ??
                  DEFAULT_HANDOVER_STATUS_FILTER,
              })
            }
            options={handoverStatusFilterOptions}
            searchPlaceholder="Zoek op status"
            emptyText="Geen statussen gevonden"
          />
        </FilterField>

        <FilterField label="Afhandeling">
          <Combobox
            aria-label="Afhandeling"
            value={destination}
            onValueChange={(value) =>
              setFilter({
                destination: value as
                  | NonNullable<VehicleHandoverDestination>
                  | undefined,
              })
            }
            options={handoverDestinationOptions}
            placeholder="Alle afhandelingen"
            searchPlaceholder="Zoek op afhandeling"
            emptyText="Geen afhandelingen gevonden"
          />
        </FilterField>
        <FilterField label="Type">
          <Combobox
            aria-label="Type"
            value={type}
            onValueChange={(value) =>
              setFilter({ type: value as VehicleHandoverType | undefined })
            }
            options={handoverTypeOptions}
            placeholder="Alle types"
            searchable={false}
          />
        </FilterField>
        <FilterField label="Te laat">
          <Combobox
            aria-label="Te laat"
            value={overdue?.toString()}
            onValueChange={(value) =>
              setFilter({ overdue: parseBooleanFilter(value) })
            }
            options={booleanFilterOptions}
            placeholder="Alle"
            searchable={false}
          />
        </FilterField>
        <FilterField label="Opvolging openstaand">
          <Combobox
            aria-label="Opvolging openstaand"
            value={followUpPending?.toString()}
            onValueChange={(value) => {
              const pending = parseBooleanFilter(value);
              // Pending follow-up only exists on completed returns, not the default open worklist.
              setFilter({
                followUpPending: pending,
                ...(pending ? { status: "completed" } : {}),
              });
            }}
            options={booleanFilterOptions}
            placeholder="Alle"
            searchable={false}
          />
        </FilterField>
        <FilterField label={handoverWorkLabels.followUpOverdue}>
          <Combobox
            aria-label={handoverWorkLabels.followUpOverdue}
            value={followUpOverdue?.toString()}
            onValueChange={(value) => {
              const late = parseBooleanFilter(value);
              setFilter({
                followUpOverdue: late,
                ...(late ? { status: "completed" } : {}),
              });
            }}
            options={booleanFilterOptions}
            placeholder="Alle"
            searchable={false}
          />
        </FilterField>
        <FilterField label="In een wissel">
          <Combobox
            aria-label="In een wissel"
            value={swap?.toString()}
            onValueChange={(value) =>
              setFilter({ swap: parseBooleanFilter(value) })
            }
            options={booleanFilterOptions}
            placeholder="Alle"
            searchable={false}
          />
        </FilterField>
      </fieldset>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th className="w-44">Kenteken</Th>
              <Th>Voertuig</Th>
              <Th>Type</Th>
              <Th className="hidden w-52 lg:table-cell">Medewerker</Th>
              <Th className="hidden w-44 xl:table-cell">Reden</Th>
              <Th className="w-48">Afhandeling</Th>
              <Th className="w-36">Gepland</Th>
              <Th className="w-40">Status</Th>
              <Th className="w-12">
                <span className="sr-only">Openen</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <VehicleHandoverRows
              isPending={isPending}
              isError={isError}
              members={data?.member}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function VehicleHandoverRows({
  isPending,
  isError,
  members,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly VehicleHandover[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-6 w-28" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-20" />
        </Td>
        <Td className="hidden lg:table-cell">
          <Skeleton className="h-4 w-32" />
        </Td>
        <Td className="hidden xl:table-cell">
          <Skeleton className="h-4 w-28" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-36" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-24 rounded-full" />
        </Td>
        <Td />
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de innames niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td
          colSpan={COLUMN_COUNT}
          className="py-10 text-center text-muted-foreground"
        >
          Geen innames gevonden.
        </Td>
      </Tr>
    );
  }

  return members.map((vehicleHandover) => (
    <Tr key={vehicleHandover.id}>
      <Td>
        <LicensePlateCell vehicleHandover={vehicleHandover} />
      </Td>
      <Td className="font-medium">{vehicleTitle(vehicleHandover)}</Td>
      <Td>
        <div className="flex flex-wrap gap-2">
          {handoverTypeLabel(vehicleHandover.type)}
          {vehicleHandover.swapId ? (
            <Badge variant="info" size="sm">
              Wissel
            </Badge>
          ) : null}
        </div>
      </Td>
      <Td className="hidden lg:table-cell">
        {vehicleHandover.employeeName ?? EMPTY_VALUE}
      </Td>
      <Td className="hidden text-muted-foreground xl:table-cell">
        {handoverReasonLabel(vehicleHandover.reason)}
      </Td>
      <Td>{handoverDestinationLabel(vehicleHandover.destination)}</Td>
      <Td className="text-muted-foreground">
        <PlannedCell vehicleHandover={vehicleHandover} />
      </Td>
      <Td>
        <div className="flex flex-wrap gap-2">
          <Badge variant={handoverStatusTone(vehicleHandover.status)} size="sm">
            {handoverStatusLabel(vehicleHandover.status)}
          </Badge>
          <VehicleHandoverFollowUpOverdueBadge
            followUpOverdue={vehicleHandover.followUpOverdue}
          />
        </div>
      </Td>
      <Td className="text-right">
        {vehicleHandover.id ? (
          <Link
            to="/vehicle-handovers/$vehicleHandoverId"
            params={{ vehicleHandoverId: vehicleHandover.id }}
            aria-label={`${handoverTypeLabel(vehicleHandover.type)} ${
              vehicleHandover.vehicleLicensePlate ??
              vehicleTitle(vehicleHandover)
            } openen`}
            className="inline-grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        ) : null}
      </Td>
    </Tr>
  ));
}

/** "Merk Model", or the empty placeholder when neither is known. */
function vehicleTitle(vehicleHandover: VehicleHandover): string {
  return (
    [vehicleHandover.vehicleBrand, vehicleHandover.vehicleModel]
      .filter(Boolean)
      .join(" ") || EMPTY_VALUE
  );
}

/**
 * The licence plate, linked to the hand-in the row is about.
 *
 * The plate is what identifies a row here, so it is also what people aim at.
 * It leads to the hand-in rather than to the vehicle, because this list is
 * worked hand-in by hand-in: the next question after "which one is this" is
 * "what is the state of it and what is my next step", and that lives on the
 * return. The vehicle is one click further, from the return itself.
 */
function LicensePlateCell({
  vehicleHandover,
}: {
  readonly vehicleHandover: VehicleHandover;
}): ReactNode {
  if (!vehicleHandover.vehicleLicensePlate) return EMPTY_VALUE;

  const plate = (
    <LicensePlate value={vehicleHandover.vehicleLicensePlate} size="sm" />
  );

  if (!vehicleHandover.id) return plate;

  return (
    <Link
      to="/vehicle-handovers/$vehicleHandoverId"
      params={{ vehicleHandoverId: vehicleHandover.id }}
      className="inline-flex rounded-md transition-opacity hover:opacity-80"
    >
      {plate}
    </Link>
  );
}

/**
 * The planned day, called out when it has come and gone.
 *
 * A day that has passed without the vehicle coming in is the one thing on this
 * list that will not solve itself, so it gets a badge rather than the plain
 * grey date every other row has. The badge says so in words as well as in
 * colour, because "this one is late" is the whole point of the marker.
 */
function PlannedCell({
  vehicleHandover,
}: {
  readonly vehicleHandover: VehicleHandover;
}): ReactNode {
  const planned = formatDateOrEmpty(vehicleHandover.plannedAt);

  if (!vehicleHandover.overdue) return planned;

  return (
    <Badge variant="warning" size="sm">
      {handoverWorkLabels.overdue}: {planned}
    </Badge>
  );
}
